import { useMemo, useEffect, useState } from "react";
import { Clock } from "lucide-react";

/**
 * Date + 15-min time slot picker.
 *
 * The value is a `datetime-local` style string ("YYYY-MM-DDTHH:MM"). An empty
 * string means **time not selected** — the parent should refuse to submit.
 *
 * Behaviour:
 *  - Date defaults to today on mount (only if `value` is empty AND no internal date was chosen yet).
 *  - Time slot dropdown is INTENTIONALLY blank by default — the user must pick one.
 *  - When the chosen date is today, the slot list starts at the next 15-min boundary after `now`.
 *  - Quick chips ("+30dk", "+1 saat", "+1.5 saat", "+2 saat") set the value relative to now,
 *    snapped to the next 15-min boundary.
 */
export function DateTimeQuickPicker({ value, onChange, testIdPrefix = "dt" }) {
  // Internal date state — used so the date field can hold an ileri-tarihli
  // seçim even when time is still empty (value is "" until BOTH date+time are
  // chosen). Must be `useState` (not `useRef`) so picking a future date
  // triggers a re-render and the memo re-computes.
  const [internalDate, setInternalDate] = useState(todayStr());

  // Whenever the parent-owned `value` changes to a full "YYYY-MM-DDTHH:MM"
  // string (e.g., via a Quick chip), keep internalDate in sync.
  useEffect(() => {
    if (value && value.includes("T")) {
      const d = value.split("T")[0];
      if (d) setInternalDate(d);
    }
  }, [value]);

  const { date, time } = useMemo(() => {
    if (value && value.includes("T")) {
      const [d, t] = value.split("T");
      return { date: d, time: (t || "").slice(0, 5) };
    }
    return { date: internalDate, time: "" };
  }, [value, internalDate]);

  const slots = useMemo(() => buildSlots(date), [date]);

  const handleDate = (d) => {
    if (!d) return;
    setInternalDate(d);
    if (time) {
      // Keep time, switch date
      const newSlots = buildSlots(d);
      if (newSlots.includes(time)) {
        onChange?.(`${d}T${time}`);
      } else {
        // Selected time invalid for new date (e.g. picked today + a past slot). Reset time.
        onChange?.("");
      }
    } else {
      onChange?.("");
    }
  };

  const handleTime = (t) => {
    if (!t) {
      onChange?.("");
      return;
    }
    onChange?.(`${date}T${t}`);
  };

  const setOffset = (mins) => {
    const d = new Date(Date.now() + mins * 60 * 1000);
    const m = d.getMinutes();
    const snapped = Math.ceil(m / 15) * 15;
    if (snapped === 60) {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0);
    } else {
      d.setMinutes(snapped);
    }
    d.setSeconds(0, 0);
    const tz = d.getTimezoneOffset() * 60000;
    const iso = new Date(d.getTime() - tz).toISOString().slice(0, 16);
    setInternalDate(iso.slice(0, 10));
    onChange?.(iso);
  };

  return (
    <div className="space-y-2">
      {/* iOS Safari `<input type="date">` fix:
          — WebKit'in varsayılan native stili genişliği ve iç hizalamayı bozar;
            asimetrik iç padding ekler ve `width:100%`'e her zaman uymaz.
          — `appearance:none` + explicit background/padding/border-radius ile
            input tam olarak parent grid hücresinin genişliğini alır ve sağ/sol
            marjinleri simetrik olur. Sadece bu bileşendeki alanları etkiler. */}
      <style>{`
        .dtqp-input {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-color: #fafafa;
          background-image: none;
          border-radius: 0;
          box-sizing: border-box;
          width: 100%;
          min-width: 0;
          text-align: left;
        }
        .dtqp-input::-webkit-date-and-time-value { text-align: left; }
      `}</style>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
        <div className="sm:col-span-6 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-1">Tarih</div>
          <input
            data-testid={`${testIdPrefix}-date`}
            type="date"
            value={date}
            min={todayStr()}
            onChange={(e) => handleDate(e.target.value)}
            className="brutalist-input dtqp-input font-mono"
          />
        </div>
        <div className="sm:col-span-6 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-1">Saat</div>
          <select
            data-testid={`${testIdPrefix}-time`}
            value={time}
            onChange={(e) => handleTime(e.target.value)}
            className={`brutalist-input dtqp-input font-mono text-center ${time ? "text-zinc-950" : "text-zinc-400"}`}
          >
            <option value="">Saat seçin…</option>
            {slots.map((s) => (
              <option key={s} value={s} className="text-zinc-950">{s}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <div className="flex items-center gap-1 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500">
          <Clock size={10} strokeWidth={2.5} />
          Hızlı
        </div>
        {[
          { label: "+30dk",     min: 30 },
          { label: "+1 saat",   min: 60 },
          { label: "+1.5 saat", min: 90 },
          { label: "+2 saat",   min: 120 },
        ].map((q) => (
          <button
            key={q.label}
            type="button"
            data-testid={`${testIdPrefix}-quick-${q.min}`}
            onClick={() => setOffset(q.min)}
            className="px-2 py-1 border-2 border-zinc-200 bg-white text-[11px] font-bold uppercase tracking-wider text-zinc-700 hover:border-zinc-950 hover:bg-zinc-50 active:translate-y-[1px] transition-all"
          >
            {q.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function todayStr() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

function buildSlots(forDate) {
  // 96 slots (00:00 → 23:45). If date is today, filter to the next slot after now.
  const isToday = forDate === todayStr();
  let startMin = 0;
  if (isToday) {
    const now = new Date();
    const minsNow = now.getHours() * 60 + now.getMinutes();
    // Next 15-min boundary STRICTLY after now (so 13:30 selects 13:45)
    startMin = Math.floor(minsNow / 15) * 15 + 15;
  }
  const out = [];
  for (let m = startMin; m < 24 * 60; m += 15) {
    const hh = String(Math.floor(m / 60)).padStart(2, "0");
    const mm = String(m % 60).padStart(2, "0");
    out.push(`${hh}:${mm}`);
  }
  return out;
}
